// File: src/pages/Work.js
import React from "react";
import { useContext } from "react";
import { Helmet } from "react-helmet";
import { Routes, Route, useNavigate } from "react-router-dom";
import { MobileViewContext } from "../context/MobileViewContext";
import SecondarySidebarBasic from "../components/SecondarySidebarBasic";
import TechStack from "./TechStack"
import DatingApp from "./DatingApp"
import DanielReport from "./DanielReport"
import "./Work.css"



export const workItems = [
  { label: "Freelance", route: "/work/freelance" },
  { label: "Experience", route: "/work/experience" },
  { label: "Collaborate", route: "/work/collaborate" },
  { label: "A New Dating App", route: "/work/datingapp" },
];

function WorkHome() {
  return (
    <div>
      <h2>Work Overview</h2>
      <p>Explore my professional background and approach.</p>
    </div>
  );
}

function Collaborate() {
  return (
    <div >
      <Helmet>
        <title>Collaborate | Helia Jamshidi</title>
        <meta
          name="description"
          content="What Helia values and avoids when working with others, and the skills she is still trying to build. If you have something in common, get in touch."
        />
        <meta
          name="keywords"
          content="Helia Jamshidi, collaboration, working style, values, radical directness, curiosity, generalists"
        />
        <meta name="author" content="Helia Jamshidi" />

        {/* Open Graph */}
        <meta property="og:title" content="Collaborate | Helia Jamshidi" />
        <meta
          property="og:description"
          content="What Helia values in working with others, and the skills she is still trying to build."
        />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/dl9xg597r/image/upload/f_webp/q_auto:low/v1738793047/EconTalk_viuikc.png"
        />
        <meta property="og:url" content="https://heliajamshidi.me/work/collaborate" />
        <meta property="og:type" content="website" />

        <link rel="canonical" href="https://heliajamshidi.me/work/collaborate" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Collaborate | Helia Jamshidi" />
        <meta
          name="twitter:description"
          content="Radical directness, curiosity, problem-making over problem-solving, and the skills Helia is still working on."
        />
        <meta
          name="twitter:image"
          content="https://res.cloudinary.com/dl9xg597r/image/upload/f_webp/q_auto:low/v1738793047/EconTalk_viuikc.png"
        />
      </Helmet>
      {/* Main Title */}
      <h1 style={{ textAlign: "left" }}>
        Collaborate:
      </h1>

      {/* Intro Section */}
      <section >
        <p >
          This is a space where I share my values and interests. If we have
          something in common, let’s chat! I enjoy all kinds of exchanges, 
          whether it’s about past experiences or brainstorming
          future ideas.
        </p>
      </section>

      {/* Values & Interests Section */}
      <section className="speaking-section-colored higher-margin">
        <h2>
          Here’s what I value (and avoid) when it comes to collaboration:
        </h2>
        <div className="list-section">
          <ul style={{ listStyleType: "none", paddingLeft: "0" }}>
            <li >
              <strong>Radical Directness + Respect + Kindness:</strong> These three
              need to go hand in hand. I’ve appreciated when others are direct
              with me, but I’m still working on being more direct myself.
            </li>

            <li >
              <strong>Yes-People:</strong> I admire those who say “yes” unless
              there’s a good reason to say “no.” I love trying new things, facing
              fears head-on, and staying optimistic about change.
            </li>

            <li >
              <strong>Generalists Over Specialists:</strong> My career heroes are
              generalists, people with broad knowledge who’ve worked across
              different fields. I’m drawn to projects that are nuanced, uncertain,
              or even seemingly impossible. Clear-cut projects where only
              execution matters? Not so much.
            </li>

            <li >
              <strong>Curiosity-Driven:</strong> Curiosity comes naturally to me.
              I’m drawn to the unknown and the complex. If a project feels too
              predictable, it doesn’t excite me as much.
            </li>

            <li >
              <strong>Problem-Making Over Problem-Solving:</strong> I used to be
              all about solving problems, fixing traffic, teaching kids about
              systems, optimizing everything. But then I discovered Tango. It
              didn’t solve any of my problems; in fact, it added more. Yet, I
              wouldn’t trade it for anything. Now, I enjoy introducing people to
              problems they didn’t know they had, in a way that makes them grateful
              for the discovery.
            </li>

            <li >
              <strong>No False Proxies:</strong> I can’t stand metrics or
              standards that pretend to measure something important but actually
              measure something trivial. When we focus on proxies, we lose sight
              of our real goals.
            </li>

            <li >
              <strong>Balanced Information Flow:</strong> I thrive in environments
              with low-to-medium information flow, where experience actually
              deepens understanding. On one end of the spectrum, you have fields
              like opera singing or brain surgery, where extreme low-info flow
              reigns. In these areas, experience is everything, and the frameworks
              don’t change much over time. On the other end, you have fields like
              AI or software engineering, where high-info flow dominates. In these
              areas, frameworks evolve so quickly that someone with 30 years of
              experience is only marginally better than a newcomer. My sweet spot
              is somewhere in the middle, where experience matters, but there’s
              still room for growth and adaptation without being overwhelmed by
              constant change.
            </li>
          </ul>
        </div>
      </section>

      <br></br>
      <section className="speaking-section-colored higher-margin">
        <h2 >
          Skills and Values I Aspire To:
        </h2>

        <ul style={{ listStyleType: "none", paddingLeft: "0" }}>
          <li >
            <strong>Experimental Thinking:</strong> I tend to overthink problems
            in isolation, coming up with logical solutions that “should” work. I
            admire people who just go out and test things, gather data, and see
            what actually works.
          </li>

          <li >
            <strong>Deeper Empathy:</strong> I know people don’t
            love what I love (and vice versa), but I still fail to act on that.
          </li>

          <li >
            <strong>Collaborating with New People:</strong> As a software
            engineer, I thrive with a fixed team that knows each other well. I
            want to get better at working with new people or customers, even in
            one-time projects.
          </li>
        </ul>
      </section>
    </div>
  );
}




const PortfolioShowcase = () => {

  return (
    <div>
      <Helmet>
        <title>Experience | Helia Jamshidi</title>
        <meta
          name="description"
          content="Helia's work as a forward deployed engineer at Quantagonia (part of Strangeworks) on DATEV's cloud migration planning and the Aura modelling agent, and earlier optimisation work at PTV Logistics."
        />
        <meta
          name="keywords"
          content="Helia Jamshidi, forward deployed engineer, Quantagonia, Strangeworks, DATEV, AI agents, Claude Agent SDK, MCP, mixed integer programming, PTV, route optimization"
        />
        <meta name="author" content="Helia Jamshidi" />

        {/* Open Graph */}
        <meta property="og:title" content="Experience | Helia Jamshidi" />
        <meta
          property="og:description"
          content="Forward deployed delivery at DATEV, autonomous optimisation agents, and Europe's fastest route optimisation solver."
        />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/dl9xg597r/image/upload/v1739045146/geocoding_rbcpgs.png"
        />
        <meta property="og:url" content="https://heliajamshidi.me/work/experience" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Experience | Helia Jamshidi" />
        <meta
          name="twitter:description"
          content="Forward deployed engineering at Quantagonia, AI agent pipelines, and large scale optimisation."
        />
        <meta
          name="twitter:image"
          content="https://res.cloudinary.com/dl9xg597r/image/upload/v1739045146/geocoding_rbcpgs.png"
        />

        <link rel="canonical" href="https://heliajamshidi.me/work/experience" />
      </Helmet>

      <section className="portfolio-showcase">
        <h1>Experience</h1>
        <p className="portfolio-intro">
          For the past year I have worked as a forward deployed engineer at
          <strong> Quantagonia</strong> (part of <strong>Strangeworks</strong>),
          sitting on the customer side of a large scale optimisation product and
          owning everything between their problem and a system running in their
          production. Alongside delivery I run discovery calls and in person
          customer workshops to open new accounts, including a paid proof of
          concept with a Tier 1 automotive supplier. Before that came six months
          of independent work and the Antler Berlin cohort, and four and a half
          years of decision optimisation at PTV Logistics.
        </p>

        <div className="portfolio-section" id="datev">
          <div className="portfolio-content">
            <h2>Cloud Migration Planning for DATEV</h2>
            <p>
              I led the delivery team for <strong>DATEV</strong>'s cloud migration
              planning product, and later ran the account on my own: every
              customer call, requirement, escalation and release. I sat in the
              client's planning rounds several times a month and drove the
              handover until DATEV operated the product entirely self service.
            </p>
            <p>
              The application plans <strong>hundreds of thousands of customers per
              half year wave</strong> across up to twenty parallel migrations and a
              partner network of tens of thousands, on a horizon reaching 2030.
              Underneath it is a mixed integer program with seven objectives and
              twenty one families of hard constraints, running into
              <strong> millions of rows</strong> on Quantagonia's HybridSolver and
              HiGHS. The first wave is now in execution, with committed batches
              held fixed as the plan is redrawn around them. Over fifteen months
              the codebase grew to about 110,000 lines of production Python, held
              in place by some 4,700 automated tests across five levels, from unit
              tests to browser runs and solver benchmarks, and shipped in more
              than ninety tagged releases.
            </p>
            <p>
              By the end of it I had automated most of my own delivery loop:
              customer emails and meeting notes become <strong>Linear tickets</strong>,
              tickets feed a context loaded coding agent harness built on
              <strong> Claude</strong> and <strong>Codex</strong>, with fourteen
              project skills and seven specialised agents, and out the other side
              comes a reviewed pull request.
            </p>
          </div>
        </div>

        <div className="portfolio-section" id="aura">
          <div className="portfolio-content">
            <h2>Aura: an Autonomous Modelling Agent</h2>
            <p>
              With the team I worked on the core of <strong>Aura</strong>, an agent
              that turns a described business problem into a working optimisation
              model, using the <strong>Claude Agent SDK</strong> and
              <strong> MCP</strong>. It runs as a modeller and judge loop with
              independent verifiers that re-derive results rather than trusting
              them.
            </p>
            <p>
              What I actually took away from it was about pace. We rewrote the
              whole agent framework <strong>four times</strong>, and each rewrite
              happened because the models had moved underneath us: every new
              generation needed <em>less</em> guidance and <em>more</em> room. We
              went from Python code, to skills, to skills we could delete because
              the models had digested what they were there to teach. So the real
              skill is planning for the capabilities you will have in six months
              rather than building tightly around the ones you have today.
            </p>
            <p>
              The other half was reliability. A single agent run can be
              outstanding and then unrepeatable. Getting from that to
              <em> consistently</em> good came from structure rather than
              cleverness: clear boundaries between agents, handing work off as
              artifacts instead of conversation, and explicit ownership and
              accountability at each step, so that a good result stops being a
              matter of luck.
            </p>
          </div>
        </div>

        <h2 className="portfolio-divider">Earlier: PTV Logistics</h2>

        <div className="portfolio-section" id="geocoding">
          <div className="portfolio-content">
            <h2>Geocoding Developer API</h2>
            <p>
              At PTV Logistics, I worked on the <strong>Geocoding Developer API</strong>, a powerful tool that transforms free-form text into precise geographic coordinates. This API is the backbone of location-based services, enabling businesses to optimize logistics, improve delivery accuracy, and enhance customer experiences.
            </p>
            <p>
              I optimized the geocoding logic using <strong>Quarkus</strong> and <strong>Java</strong>, ensuring high performance and scalability. Additionally, I integrated <strong>Azure ML pipelines</strong> to train and evaluate large language models (LLMs), significantly improving the API's accuracy and efficiency.
            </p>
            <a href="https://developer.myptv.com/en/documentation/geocoding-places-api" target="_blank" rel="noopener noreferrer">
              Discover more
            </a>
          </div>
          <div className="portfolio-visual">
            <img
              src="https://res.cloudinary.com/dl9xg597r/image/upload/v1739045146/geocoding_rbcpgs.png"
              alt="Geocoding API Demo"
            />
          </div>
        </div>
        <div className="portfolio-section" id="model2go">
          <div className="portfolio-content">
            <h2>PTV Model2Go</h2>
            <p>
              <strong>PTV Model2Go</strong> is a cutting-edge solution for automated transport model generation. I played a key role in developing and maintaining Python modules that power this product, enabling seamless integration with APIs and serverless jobs.
            </p>
            <p>
              I also established a robust testing framework, increasing test coverage from <strong>0% to 80%</strong>, ensuring a high-quality codebase. This product is a game-changer for logistics companies, allowing them to create accurate transport models in minutes.
            </p>
            <a href="https://www.ptvgroup.com/en/products/ptv-model2go" target="_blank" rel="noopener noreferrer">
              Explore Model2Go
            </a>
          </div>
          <div className="portfolio-visual">
            <img
              src="https://res.cloudinary.com/dl9xg597r/image/upload/v1739045147/model2go_wa1iz8.jpg"
              alt="PTV Model2Go Demo"
            />
          </div>
        </div>
        <div className="portfolio-section" id="route-optimisation">
          <div className="portfolio-content">
            <h2>Route Optimisation API & Route-Optimiser</h2>
            <p>
              The <strong>Route Optimization API</strong> is a strategic product for PTV, solving complex vehicle routing problems (VRP). I contributed to the development of a meta-heuristic solver, restructuring the existing <strong>C++</strong> codebase to integrate state-of-the-art academic algorithms.
            </p>
            <p>
              This API helps logistics companies reduce costs, minimize delivery times, and improve resource utilization. My work ensured that the solver could handle real-world constraints, such as time windows and vehicle capacities, with exceptional efficiency.
            </p>
            <a href="https://developer.myptv.com/en/documentation/route-optimization-api/quick-start-route-optimisation-api" target="_blank" rel="noopener noreferrer">
              See the solution in action
            </a>
          </div>
          <div className="portfolio-visual">
            <img
              src="https://res.cloudinary.com/dl9xg597r/image/upload/v1739045146/routeoptimizer_i3i1r5.webp"
              alt="Route Optimisation Demo"
            />
          </div>
        </div>
        <div className="portfolio-section" id="optiflow">
          <div className="portfolio-content">
            <h2>PTV Optiflow</h2>
            <p>
              <strong>PTV Optiflow</strong> is a SaaS product designed to optimize large-scale logistics operations. I worked on a new microservice for this product, leveraging <strong>AWS</strong> and <strong>Spring Boot</strong> to build a scalable and reliable solution.
            </p>
            <p>
              My contributions included implementing <strong>Domain-Driven Design (DDD)</strong> principles and collaborating with a team using <strong>Mob Programming</strong>. This ensured that the microservice met the highest standards of quality and performance.
            </p>
            <a href="https://developer.myptv.com/en/documentation/route-optimization-optiflow-api/quick-start-route-optimization-optiflow" target="_blank" rel="noopener noreferrer">
              Learn about Optiflow
            </a>
          </div>
          <div className="portfolio-visual">
            <img
              src="https://res.cloudinary.com/dl9xg597r/image/upload/v1739045146/optiflow_cnxtrr.jpg"
              alt="PTV Optiflow Demo"
            />
          </div>
        </div>
      </section>
    </div>
  );
};


function Freelance() {

  const navigate = useNavigate();

  const handleBookTime = () => {
    navigate('/interactive/book');
  };

  return (
    <div>
      <Helmet>
        <title>Freelance Services | Helia Jamshidi</title>
        <meta
          name="description"
          content="Selective freelance and advisory work in AI agents, large scale optimisation, and forward deployed delivery, alongside my role at Quantagonia."
        />
        <meta
          name="keywords"
          content="Helia Jamshidi, freelance, advisory, AI agents, Claude Agent SDK, MCP, operations research, mixed integer programming, forward deployed engineer, microservices"
        />
        <meta name="author" content="Helia Jamshidi" />

        {/* Open Graph */}
        <meta property="og:title" content="Freelance Services | Helia Jamshidi" />
        <meta
          property="og:description"
          content="Helia takes on a small number of engagements in agent systems, discrete optimisation, and getting technical products in front of real customers."
        />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/dl9xg597r/image/upload/f_webp/q_auto:low/v1738793047/EconTalk_viuikc.png"
        />
        <meta property="og:url" content="https://heliajamshidi.me/work/freelance" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Freelance Services | Helia Jamshidi" />
        <meta
          name="twitter:description"
          content="A few engagements at a time: AI agents that can be trusted, discrete optimisation at scale, and forward deployed delivery."
        />
        <meta
          name="twitter:image"
          content="https://res.cloudinary.com/dl9xg597r/image/upload/f_webp/q_auto:low/v1738793047/EconTalk_viuikc.png"
        />

        <link rel="canonical" href="https://heliajamshidi.me/work/freelance" />
      </Helmet>

      <h1>Freelance</h1>
      <section className="freelance-intro" style={{ marginBottom: "3rem" }}>
        <p>
          I currently work as a <em>forward deployed engineer</em> at <em>Quantagonia</em> (part of <em>Strangeworks</em>), where I owned the delivery of a cloud migration planning product for <em>DATEV</em>: the customer calls, the optimisation model, the platform, and the go live. Before that I spent four and a half years at <em>PTV Logistics</em> on route optimisation for supermarkets and delivery companies across Germany and the Benelux.
        </p>
        <p>
          That is a full time job, so I take on <em>a small number of engagements at a time</em>, usually advisory or scoped project work, and I am honest up front about what I can fit. If the problem is interesting I will tell you; if I am not the right person I will say that too, and try to point you somewhere better.
        </p>
      </section>
      <section className="speaking-section-colored">
        <h2>You can <em>trust</em> me with:</h2>
        <div className="list-section">
          <h3>AI Agents</h3>
          <ul>
            <li>Building agent systems with the <em>Claude Agent SDK</em> and <em>MCP</em>: multi-agent pipelines, judge and verifier loops, tool design.</li>
            <li>Evals and integrity tooling, because the hard part is not making an agent work, it is catching the runs where it only claims to have worked.</li>
            <li>Putting agents into an existing delivery loop: tickets, context loading, coding agent harnesses, reviewed pull requests.</li>
            <li>An honest second opinion on whether a problem needs an agent at all. Often it does not.</li>
          </ul>
        </div>
        <div className="list-section">
          <h3>Operations Research</h3>
          <ul>
            <li>Solving your discrete (integer) optimization problems with linear programming and modern metaheuristics.</li>
            <li>Modelling at scale: multi-objective problems with large constraint families, on HiGHS, SCIP, Gurobi, CP-SAT, or Quantagonia's HybridSolver.</li>
            <li>Consulting to speed up your current discrete optimization solver.</li>
            <li>Combining ML/AI with traditional metaheuristics.</li>
          </ul>
        </div>
        <div className="list-section">
          <h3>Forward Deployed Delivery</h3>
          <ul>
            <li>Sitting on your customer's side of a technical product: discovery, requirements, delivery, go live, and the postmortem afterwards.</li>
            <li>Running the workshop where a room of domain experts turns into a model that survives contact with their real data.</li>
            <li>Handover, so the customer ends up operating the thing themselves instead of depending on you forever.</li>
          </ul>
        </div>
        <div className="list-section">
          <h3>ML / AI</h3>
          <ul>
            <li>Setting up your ML pipelines, from data gathering to evaluation, monitoring, and impact (Azure ML).</li>
            <li>Fine-tuning open-source <em>foundation models</em> for your needs and deploying them on your local cloud.</li>
          </ul>
        </div>
        <div className="list-section">
          <h3>Backend / SaaS / APIs</h3>
          <ul>
            <li>Building back-end services in Python (Django, FastAPI, Celery, Flask), or on the JVM in Quarkus and Spring Boot.</li>
            <li>Taking a codebase from untestable to testable: layering, verifier and end to end suites, infrastructure as code on AWS with Terraform and Ansible, observability with OpenTelemetry.</li>
            <li>Providing minimal front-end demos to test and showcase the project (not intended for final users).</li>
          </ul>
        </div>
      </section>
      <section className="call-to-action"
        style={{ marginTop: "4rem", textAlign: "center", padding: "3rem", borderRadius: "8px" }}>
        <p style={{ fontSize: "1.2rem", maxWidth: "600px", margin: "0 auto", marginBottom: "2rem" }}>
          If you need expert help with agents, optimisation, or getting a technical
          product in front of real customers, let's talk!
          I’m also happy to be recommended to anyone who could benefit from my skills.
        </p>
        <button type="submit" className="submit-button" onClick={handleBookTime}>
          Book an Introductory Session
        </button>
      </section>
    </div>
  );
}



export default function Work({ isMobile }) {
  const { contentIsVisibleMobile, toc2IsVisibleMobile } = useContext(MobileViewContext);
  return (
    <div className="content-with-sub">
      {(!isMobile || toc2IsVisibleMobile) &&
        <SecondarySidebarBasic heading="Work" items={workItems} />}
      {(!isMobile || contentIsVisibleMobile) &&
        <div className="content-area">
          <Routes>
            <Route path="/" element={<WorkHome />} />
            <Route path="/freelance" element={<Freelance />} />
            <Route path="/experience" element={
              <div style={{ padding: "0px", margin: "0px" }}>
                <PortfolioShowcase />
                <TechStack />
              </div>
            }></Route>
            <Route path="/collaborate" element={<Collaborate />} />
            <Route path="/datingapp" element={<DatingApp />} />
            <Route path="/daniel" element={<DanielReport />} />
          </Routes>
        </div>}
    </div>
  );
}
